// N3's kind × channel matrix (design/notifications-v2.dc.html), shared by
// the desktop settings screen and its mobile twin. The defaults and the
// locked cells are the Ruby side's (User::NOTIFICATION_PREF_DEFAULTS /
// NOTIFICATION_PREF_LOCKED), handed over on the city config's
// `notifications` block by PagesController#city_config_payload and installed
// here by useNotificationMatrix() — the server is the authority; this copy
// only decides what the toggles draw before the jsonb has an entry. The
// tables below are the unreachable fallback for a payload without the block.
//
// Channel keys are the server's: push / email / sms (the UI says "Text").
import { useContext, useMemo } from 'react';
import ConfigContext from '../../../contexts/ConfigContext';

export const CHANNELS = ['push', 'email', 'sms'];

export const CHANNEL_LABELS = { push: 'Push', email: 'Email', sms: 'Text' };

export const MATRIX_ROWS = [
  { kind: 'document_needs_fix', label: 'A document needs a fix' },
  { kind: 'catering_request_new', label: 'A new catering request' },
  { kind: 'booking_confirmed_or_cancelled', label: 'A booking is confirmed or canceled' },
  { kind: 'payout_sent', label: 'A payout is sent' },
  { kind: 'permit_expiring', label: 'A permit is expiring' },
  { kind: 'instagram_disconnected', label: 'Instagram disconnected' },
  { kind: 'review_new', label: 'A new review' },
  { kind: 'message_new', label: 'A new message' },
  { kind: 'weekly_pod_digest', label: 'Weekly spot openings digest' },
  { kind: 'sales_report_due', label: 'Sales report due' },
  { kind: 'card_charge_receipt', label: 'Card charges & receipts' },
];

// Fallbacks (mirror of the Ruby constants as of 7 Sep 2026).
const FALLBACK_LOCKED = {
  sales_report_due: ['push', 'email'],
  card_charge_receipt: ['email'],
  booking_confirmed_or_cancelled: ['sms'],
};

const FALLBACK_DEFAULTS = {
  document_needs_fix: { push: true, email: true, sms: true },
  catering_request_new: { push: true, email: true, sms: true },
  booking_confirmed_or_cancelled: { push: true, email: true, sms: true },
  payout_sent: { push: true, email: true, sms: false },
  permit_expiring: { push: true, email: true, sms: false },
  instagram_disconnected: { push: true, email: true, sms: false },
  review_new: { push: false, email: false, sms: false },
  message_new: { push: true, email: true, sms: false },
  weekly_pod_digest: { push: false, email: true, sms: false },
  sales_report_due: { push: true, email: true, sms: false },
  card_charge_receipt: { push: false, email: true, sms: false },
};

let active = null;
let activeSource = null;

function normalizeMatrix(block) {
  const defaults = block && block.pref_defaults && Object.keys(block.pref_defaults).length > 0
    ? block.pref_defaults : FALLBACK_DEFAULTS;
  const locked = block && block.pref_locked && Object.keys(block.pref_locked).length > 0
    ? block.pref_locked : FALLBACK_LOCKED;
  return { defaults, locked };
}

export function installNotificationMatrix(block) {
  if (active && (!block || block === activeSource)) return active;
  activeSource = block || null;
  active = normalizeMatrix(block);
  return active;
}

export function notificationMatrix() {
  return active || installNotificationMatrix(null);
}

// The raw `notifications` block as installed (null before a vendor surface
// rendered under ConfigContext) — notificationDisplay reads kind_filters off it.
export function notificationMatrixSource() {
  return activeSource;
}

// ConfigContext.notifications, installed for the pure helpers below.
export function useNotificationMatrix() {
  const config = useContext(ConfigContext) || {};
  const block = config.notifications || null;
  return useMemo(() => installNotificationMatrix(block), [block]);
}

// Cells that can never be off: the two sends that cost money to miss, and
// the cancellation text (you need to know before you drive).
export function lockedChannels(kind) {
  return notificationMatrix().locked[kind] || [];
}

export function defaultRow(kind) {
  return notificationMatrix().defaults[kind] || {};
}

export function isLocked(kind, channel) {
  return lockedChannels(kind).indexOf(channel) !== -1;
}

// The value a toggle should draw: locked → on; explicit jsonb entry → that;
// otherwise the frame default.
export function cellValue(prefs, kind, channel) {
  if (isLocked(kind, channel)) return true;
  const row = (prefs || {})[kind];
  if (row && typeof row[channel] === 'boolean') return row[channel];
  return Boolean(defaultRow(kind)[channel]);
}

// The full row to persist after one cell flips. merge_notification_prefs!
// replaces a kind's row wholesale, so every write carries all three
// channels — sending only the flipped one would drop the other two.
export function rowAfterToggle(prefs, kind, channel, value) {
  const next = {};
  CHANNELS.forEach((c) => {
    next[c] = c === channel ? value : cellValue(prefs, kind, c);
  });
  return next;
}

// "10 PM" / "7 AM" for the quiet-hours sentence.
export function hourLabel(hour) {
  const h = ((Number(hour) % 24) + 24) % 24;
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}
